import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * AITracker - Agent de Tracking Invisible
 * ========================================
 * Collecte les données comportementales pour le scoring des leads.
 * Complètement invisible pour l'utilisateur.
 */

// Générer un ID visiteur unique
const getVisitorId = () => {
  let visitorId = localStorage.getItem("jyvis_visitor_id");
  if (!visitorId) {
    visitorId = `v_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem("jyvis_visitor_id", visitorId);
  }
  return visitorId;
};

// Générer un ID session
const getSessionId = () => {
  let sessionId = sessionStorage.getItem("jyvis_session_id");
  if (!sessionId) {
    sessionId = `s_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem("jyvis_session_id", sessionId);
  }
  return sessionId;
};

export default function AITracker() {
  const location = useLocation();
  const startTime = useRef(Date.now());
  const maxScroll = useRef(0);
  const lastTrack = useRef(0);

  // Fonction de tracking
  const track = useCallback(async (eventType, data = {}) => {
    // Throttle tracking to avoid too many requests
    const now = Date.now();
    if (now - lastTrack.current < 500 && eventType !== "page_view") {
      return;
    }
    lastTrack.current = now;

    try {
      await axios.post(`${API}/api/ai/track`, {
        visitor_id: getVisitorId(),
        session_id: getSessionId(),
        event_type: eventType,
        data: {
          ...data,
          timestamp: new Date().toISOString(),
          url: window.location.href,
          referrer: document.referrer
        }
      });
    } catch (e) {
      // Silent fail - tracking ne doit pas impacter l'UX
    }
  }, []);

  // Track page views
  useEffect(() => {
    track("page_view", { 
      page: location.pathname,
      search: location.search
    });
    
    // Reset start time for new page
    startTime.current = Date.now();
    maxScroll.current = 0;
  }, [location.pathname, track]);

  // Track time on page
  useEffect(() => {
    const timeInterval = setInterval(() => {
      const timeOnPage = Math.floor((Date.now() - startTime.current) / 1000);
      if (timeOnPage % 30 === 0 && timeOnPage > 0) { // Every 30 seconds
        track("time_update", { 
          seconds: timeOnPage,
          page: location.pathname
        });
      }
    }, 1000);

    return () => clearInterval(timeInterval);
  }, [location.pathname, track]);

  // Track scroll depth
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.round((scrollTop / docHeight) * 100);
      
      if (scrollPercent > maxScroll.current) {
        maxScroll.current = scrollPercent;
        
        // Track at milestones: 25%, 50%, 75%, 90%
        if ([25, 50, 75, 90].includes(scrollPercent)) {
          track("scroll", { 
            depth: scrollPercent,
            page: location.pathname
          });
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname, track]);

  // Track form interactions
  useEffect(() => {
    const handleFocus = (e) => {
      const target = e.target;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT") {
        const fieldName = target.name || target.id || target.placeholder || "unknown";
        track("form_interaction", {
          field: fieldName,
          action: "focus",
          started: true
        });
      }
    };

    const handleChange = (e) => {
      const target = e.target;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT") {
        const fieldName = target.name || target.id || target.placeholder || "unknown";
        
        // Detect special fields
        if (fieldName.toLowerCase().includes("plaque") || fieldName.toLowerCase().includes("immatriculation")) {
          track("form_interaction", {
            field: "license_plate",
            license_plate_entered: true
          });
        }
        
        if (fieldName.toLowerCase().includes("email") || fieldName.toLowerCase().includes("phone") || fieldName.toLowerCase().includes("tel")) {
          track("form_interaction", {
            field: "contact",
            contact_entered: true
          });
        }
      }
    };

    // Track file uploads
    const handleFileChange = (e) => {
      if (e.target.type === "file" && e.target.files?.length > 0) {
        track("form_interaction", {
          field: "file_upload",
          uploaded_document: true,
          file_name: e.target.files[0].name
        });
      }
    };

    document.addEventListener("focusin", handleFocus, true);
    document.addEventListener("change", handleChange, true);
    document.addEventListener("change", handleFileChange, true);

    return () => {
      document.removeEventListener("focusin", handleFocus, true);
      document.removeEventListener("change", handleChange, true);
      document.removeEventListener("change", handleFileChange, true);
    };
  }, [track]);

  // Track clicks on important elements
  useEffect(() => {
    const handleClick = (e) => {
      const target = e.target.closest("a, button, [data-track]");
      if (!target) return;

      const trackData = {
        element: "unknown",
        text: target.innerText?.substring(0, 50),
        href: target.href || null
      };

      // Detect WhatsApp clicks
      if (target.href?.includes("wa.me") || target.href?.includes("whatsapp")) {
        trackData.element = "whatsapp";
        track("click", trackData);
      }
      
      // Detect phone clicks
      else if (target.href?.startsWith("tel:")) {
        trackData.element = "phone";
        track("click", trackData);
      }
      
      // Detect form submissions
      else if (target.type === "submit" || target.innerText?.toLowerCase().includes("devis") || target.innerText?.toLowerCase().includes("sousc")) {
        trackData.element = "form_submit";
        track("form_interaction", {
          ...trackData,
          submitted: true
        });
      }

      // Detect CTA clicks
      else if (target.closest("[data-testid]")) {
        trackData.element = target.closest("[data-testid]").getAttribute("data-testid");
        track("click", trackData);
      }
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [track]);

  // Track before user leaves
  useEffect(() => {
    const handleBeforeUnload = () => {
      const timeOnPage = Math.floor((Date.now() - startTime.current) / 1000);
      
      // Use sendBeacon for reliable tracking on page exit
      const data = {
        visitor_id: getVisitorId(),
        session_id: getSessionId(),
        event_type: "page_exit",
        data: {
          time_on_page: timeOnPage,
          max_scroll: maxScroll.current,
          page: location.pathname
        }
      };
      
      navigator.sendBeacon(`${API}/api/ai/track`, JSON.stringify(data));
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [location.pathname]);

  // Invisible component - no render
  return null;
}

/**
 * Hook pour récupérer le score du lead actuel
 */
export const useLeadScore = () => {
  const getScore = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/ai/score/${getVisitorId()}`);
      return response.data;
    } catch (e) {
      return null;
    }
  }, []);

  return { getScore, visitorId: getVisitorId() };
};

/**
 * Hook pour récupérer les recommandations personnalisées
 */
export const useRecommendations = () => {
  const getRecommendations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/ai/recommend/${getVisitorId()}`);
      return response.data;
    } catch (e) {
      return null;
    }
  }, []);

  return { getRecommendations };
};
